import {
  EVENT_ALERTS,
  EVENT_ALERT_CONFIGURATIONS,
  SUMMARY,
  ACKNOWLEDGE,
  ASSIGN,
  RESOLVE,
  EFFECTIVE,
} from "../constants/apis";

const EMPTY_LIST_ENVELOPE = { data: [], total: 0, page: 1, limit: 20 };

const EMPTY_SUMMARY = {
  open: 0,
  acknowledged: 0,
  active: 0,
  critical: 0,
  warning: 0,
  info: 0,
  by_category: {},
  items: [],
};

// Strips undefined/null/"" so absent filters are never sent as empty query
// params (the backend validates known query keys strictly, e.g. `status`).
const withoutEmpty = (obj = {}) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

// WINERY-scoped configurations must omit `scope_id` entirely (the backend
// treats a WINERY scope's id as implicit/null; sending one is a contract
// violation, not just a no-op).
const buildScopeParams = ({ scope_type, scope_id, ...rest } = {}) =>
  withoutEmpty({
    ...rest,
    scope_type,
    ...(scope_type === "WINERY" ? {} : { scope_id }),
  });

// GETs rethrow (like the mutations below): swallowing the error into an
// empty envelope would make a 403/404/network/5xx indistinguishable from a
// genuinely empty result, and Home/Action Center need to tell those apart
// to show an error state instead of silently rendering "no alerts".
const getEventAlerts = async (filters = {}, axiosInstance) => {
  try {
    const response = await axiosInstance.get(EVENT_ALERTS, {
      withCredentials: true,
      params: withoutEmpty(filters),
    });
    return response?.data || EMPTY_LIST_ENVELOPE;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const getEventAlertsSummary = async (axiosInstance) => {
  try {
    const response = await axiosInstance.get(`${EVENT_ALERTS}${SUMMARY}`, {
      withCredentials: true,
    });
    return response?.data || EMPTY_SUMMARY;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const getEventAlertById = async (id, axiosInstance) => {
  try {
    const response = await axiosInstance.get(`${EVENT_ALERTS}/${id}`, {
      withCredentials: true,
    });
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const acknowledgeEventAlert = async (id, axiosInstance) => {
  try {
    const response = await axiosInstance.patch(
      `${EVENT_ALERTS}/${id}${ACKNOWLEDGE}`,
      undefined,
      { withCredentials: true }
    );
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const assignEventAlert = async (id, assigneeId, axiosInstance) => {
  try {
    const response = await axiosInstance.patch(
      `${EVENT_ALERTS}/${id}${ASSIGN}`,
      { assignee_id: assigneeId },
      { withCredentials: true }
    );
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const resolveEventAlert = async (id, resolutionReason, axiosInstance) => {
  try {
    const response = await axiosInstance.patch(
      `${EVENT_ALERTS}/${id}${RESOLVE}`,
      { resolution_reason: resolutionReason },
      { withCredentials: true }
    );
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const getEffectiveEventAlertConfiguration = async (params = {}, axiosInstance) => {
  try {
    const response = await axiosInstance.get(`${EVENT_ALERT_CONFIGURATIONS}${EFFECTIVE}`, {
      withCredentials: true,
      params: buildScopeParams(params),
    });
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const saveEventAlertConfiguration = async (data, axiosInstance) => {
  try {
    const response = await axiosInstance.put(EVENT_ALERT_CONFIGURATIONS, data, {
      withCredentials: true,
    });
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const deleteEventAlertConfiguration = async (params = {}, axiosInstance) => {
  try {
    const response = await axiosInstance.delete(EVENT_ALERT_CONFIGURATIONS, {
      withCredentials: true,
      params: buildScopeParams(params),
    });
    return response?.data || null;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export {
  getEventAlerts,
  getEventAlertsSummary,
  getEventAlertById,
  acknowledgeEventAlert,
  assignEventAlert,
  resolveEventAlert,
  getEffectiveEventAlertConfiguration,
  saveEventAlertConfiguration,
  deleteEventAlertConfiguration,
};
